/* eslint-disable prettier/prettier */
import React, { useEffect, useMemo, useState } from 'react';
import { useHeader } from '../../context/HeaderContext';
import { useDashboard } from '../../hooks/useDashboard'; // 🎣 1. IMPORT THE HOOK
import { Button } from '@mui/material'; // Using a simple button for the error state

// Your UI Components
import AdminActionItems from '../../components/pageComponents/dashboard/AdminActionItems';
import AdminActionItemCard from '../../components/pageComponents/dashboard/AdminActionItemsCard';

import { CalendarEvent } from '../../components/pageComponents/dashboard/WeeklyCalander';
import styles from '../../styles/pages/AdminDashboard.module.css';

// Your Icons
import { ReactComponent as ResourcesIcon } from '../../assets/icons/resources.svg';
import { ReactComponent as UpcomingSessionsIcon } from '../../assets/icons/workshop.svg';
import { useAdminDashboardAnalytics } from '../../hooks/useAdminDashboardAnalytics';

import { ReactComponent as AssessmentsIcon } from '../../assets/icons/activeMentors.svg'; // Using a placeholder icon
import LearnerProgressCard from '../../components/pageComponents/dashboard/admin/LearnerProgressCard';
import DonutChart from '../../components/pageComponents/dashboard/admin/DonutChart';
import EngagementChart from '../../components/pageComponents/dashboard/admin/EngagementChart';
import NPSChart from '../../components/pageComponents/dashboard/admin/NPSChart';
import LearnerProgressCard2 from '../../components/pageComponents/dashboard/admin/LearnerProgressCard2';
import ProgramTable from '../../components/pageComponents/dashboard/admin/AdminProgramsOverview';
import DashboardActivity from '../../components/pageComponents/dashboard/admin/DashboardActivity';
import { useUpcomingSessions } from '../../hooks/useUpcomingSessions';
import { useUserProfile } from '../../hooks/useUserProfile';

const NavAdminDashboard: React.FC = () => {
	// 🎣 2. USE THE HOOK TO GET DATA AND MANAGE STATE
	const { dashboardData, isLoading, error, refreshDashboard } =
		useDashboard();
	const {
		analytics,
		loading: analyticsLoading,
		error: analyticsError,
		fetchAnalytics,
	} = useAdminDashboardAnalytics();

	const {
		sessions,
		
		refreshUpcomingSessions,
	} = useUpcomingSessions(50);
	
	// const upcomingSessionsCount = sessions?.length || 0;
	const upcomingSessionsCount = Number(sessions?.[0]?.total || 0);
	const { setHeaderContent } = useHeader();
	const { profile } = useUserProfile();

const userName = profile?.fullname?.split(' ')[0] ?? 'User';
const headerNode = useMemo(
    () => <p>Welcome {userName}</p>,
    [userName]
);

useEffect(() => {
    setHeaderContent(headerNode);
}, [setHeaderContent, headerNode]);

	useEffect(() => {
		fetchAnalytics();
	}, []);

	
	const [isTablet, setIsTablet] = useState(false);
	useEffect(() => {
		const handleResize = () => {
			setIsTablet(window.innerWidth <= 1200);
		};

		handleResize(); // initial check
		window.addEventListener('resize', handleResize);

		return () => window.removeEventListener('resize', handleResize);
	}, []);
	
	if (isLoading) {
		return <div>Loading...</div>;
	}

	if (error) {
		return (
			<div style={{ textAlign: 'center', padding: '40px' }}>
				<p style={{ color: 'red' }}>
					Failed to load dashboard data: {error}
				</p>
				<Button variant="contained" onClick={() => refreshDashboard()}>
					Try Again
				</Button>
			</div>
		);
	}

	// Transform the session data from the API for the WeeklyCalendar component
	const calendarEvents: CalendarEvent[] =
		dashboardData?.upcomingsessions.map(session => ({
			...session, // Spread existing properties like id, title, type
			start: new Date(session.start), // Convert string from API to Date object
			end: new Date(session.end),
		})) || [];

	const data = [
		{ name: 'JAN', value: 25 },
		{ name: 'FEB', value: 20 },
		{ name: 'MAR', value: 60 },
		{ name: 'APR', value: 30 },
		{ name: 'MAY', value: 55 },
		{ name: 'JUN', value: 100 },
	];
	const data2 = [
		{ name: 'Jan', value: 8 },
		{ name: 'Feb', value: 6 },
		{ name: 'Mar', value: 10 },
		{ name: 'Apr', value: 4 },
		{ name: 'May', value: 6 },
		{ name: 'Jun', value: 8 },
	];

	const analyticsData = analytics;

	const completion = analyticsData?.learnersassignmentcompletion;
	const attendance = analyticsData?.sessionattendancerate;
	const certification = analyticsData?.certificationssuccessrate;
	const roleData = analyticsData?.roledistribution;
	const totalUsers = analyticsData?.totalusers;

	const assignmentData =
		completion?.map(item => ({
			label: item.programtitle,
			value: item.rate,
		})) || [];

	const attendanceData =
		attendance?.map(item => ({
			label: item.programtitle,
			value: item.rate,
		})) || [];

	const certificationData =
		certification?.map(item => ({
			label: item.programtitle,
			value: item.rate,
		})) || [];

	const userDonut = [
		{ name: 'Active', value: totalUsers?.active ?? 0 },
		{ name: 'Inactive', value: totalUsers?.inactive ?? 0 },
	];

	const roleDonut = [
		{ name: 'Coaches', value: roleData?.coaches ?? 0 },
		{ name: 'Learners', value: roleData?.learners ?? 0 },
	];

	const engagementData =
		analyticsData?.engagementovertime?.map(item => ({
			name: item.month,
			value: item.count,
		})) || [];

	
	const npsData = analyticsData?.netpromoterscore?.length
	? analyticsData.netpromoterscore.map(item => ({
			name: item.month,
			value: item.score,
	  }))
	: [{ name: 'No Data', value: 0 }];

	const completionRates =
		analyticsData?.completionrates?.map(item => ({
			label: item.programtitle,
			value: item.rate,
		})) || [];

	const programTableData =
		analyticsData?.completionrates?.map((item: any, index: number) => ({
			id: item.programid || index, // fallback index if no id
			title: item.programtitle,
			completionRate: item.rate,
			learners: item.learners ?? 0,
			sessions: item.sessions ?? 0,
		})) || [];

	const programColumns = [
		{
			header: 'Program Name',
			key: 'title' as const,
		},
		{
			header: 'Learners',
			key: 'learners' as any,
			render: (item: any) => <span>{item.learners}</span>,
		},
		{
			header: 'Sessions',
			key: 'sessions' as any,
			render: (item: any) => <span>{item.sessions}</span>,
		},
		{
			header: 'Completion Rate',
			key: 'completionRate' as any,
			render: (item: any) => (
				<div style={{ width: '100%' }}>
					<div
						style={{
							height: '6px',
							background: '#e5e7eb',
							borderRadius: '4px',
						}}
					>
						<div
							style={{
								width: `${item.completionRate}%`,
								height: '100%',
								background: '#147B58',
								borderRadius: '4px',
							}}
						/>
					</div>
				</div>
			),
		},
	];

	const activePrograms =
		analyticsData?.learnersassignmentcompletion?.length ?? 0;

	return (
		<div>
			{/* 🔗 4. BIND THE API DATA TO YOUR UI */}

			<div className={styles.row1}>
				<AdminActionItemCard
					type="programs"
					icon={<ResourcesIcon />}
					title="Active Programs"
					value={activePrograms}
				/>

				<AdminActionItemCard
					type="coaches"
					icon={<AssessmentsIcon />}
					title="Active Coaches"
					value={roleData?.coaches ?? 0}
				/>

				<AdminActionItemCard
					type="learners"
					icon={<AssessmentsIcon />}
					title="Active Learners"
					value={roleData?.learners ?? 0}
				/>

				{!isTablet && (
					<AdminActionItemCard
						type="sessions"
						icon={<UpcomingSessionsIcon />}
						title="Upcoming Sessions"
						value={upcomingSessionsCount}
					/>
				)}
			</div>

			<div className={styles.row2}>
				<LearnerProgressCard
					title="Learners Assignment Completion"
					count={completion?.length ?? 0}
					width="100%"
					barColor="#F97316"
					trackColor="#EF7F1A40" // grey
					data={assignmentData}
				/>
				<LearnerProgressCard
					title="Session Attendance Rate"
					count={attendance?.length ?? 0}
					width="100%"
					barColor="#1976d2"
					trackColor="#1976D240"
					data={attendanceData}
				/>
				<LearnerProgressCard
					title="Certification Success Rate"
					count={certification?.length ?? 0}
					width="100%"
					barColor="#147b58"
					trackColor="#C4DED5"
					data={certificationData}
				/>
			</div>

			<div className={styles.row3}>
				{isTablet ? (
					<>
						{/* TOP: 2 Donuts */}
						<div className={styles.topDonuts}>
							<DonutChart
								title="Total Users"
								subtitle="Users actively learning"
								height={250}
								data={userDonut}
								colors={['#1ABC9C', '#2E86DE']}
								centerText={
									(totalUsers?.active ?? 0) +
									(totalUsers?.inactive ?? 0)
								}
							/>
							<DonutChart
								title="Total Roles"
								subtitle="User distribution"
								height={250}
								data={roleDonut}
								colors={['#EF7F1A', '#5B1871']}
								centerText={
									(roleData?.coaches ?? 0) +
									(roleData?.learners ?? 0)
								}
							/>
						</div>

						{/* BOTTOM: Graph */}
						<div className={styles.fullWidth}>
							<EngagementChart
								title="Engagement over time"
								subtitle="User engagement"
								data={engagementData}
								height={300}
								lineColor="#147B58"
								gradientFrom="rgba(20,123,88,0.4)"
								gradientTo="rgba(20,123,88,0)"
								showGrid={true}
								showTooltip={true}
							/>
						</div>
					</>
				) : (
					<>
						{/* DESKTOP: 3 in row */}
						<DonutChart
							title="Total Users"
							subtitle="Users actively learning"
							height={250}
							data={userDonut}
							colors={['#1ABC9C', '#2E86DE']}
							centerText={
								(totalUsers?.active ?? 0) +
								(totalUsers?.inactive ?? 0)
							}
						/>
						<EngagementChart
							title="Engagement over time"
							subtitle="User engagement"
							data={engagementData}
							lineColor="#147B58"
							gradientFrom="rgba(20,123,88,0.4)"
							gradientTo="rgba(20,123,88,0)"
							showGrid={true}
							showTooltip={true}
						/>
						<DonutChart
							title="Total Roles"
							subtitle="User distribution"
							height={250}
							data={roleDonut}
							colors={['#EF7F1A', '#5B1871']}
							centerText={
								(roleData?.coaches ?? 0) +
								(roleData?.learners ?? 0)
							}
						/>
					</>
				)}
			</div>

			<div className={styles.row4}>
				<NPSChart
					title="Net Promoter Score"
					data={npsData}
					barColor="#10b981"
					height={220}
					borderRadius="16px"
					padding="20px"
				/>
				<LearnerProgressCard2
					title="Completion Rates"
					count={completionRates.length}
					width="100%"
					barColor="#147b58"
					trackColor="#C4DED5"
					data={completionRates}
				/>
			</div>

			<div className={styles.row5}>
				<ProgramTable />
			</div>

			<div className={styles.row5}>
				<DashboardActivity />
			</div>
		</div>
	);
};

export default NavAdminDashboard;
